import { Dispatch } from 'react'
import {
    authRefreshPath,
    retrieveUserInfoToken,
    getAuthStateFromUserInfoToken,
    AccessTokenState,
} from '../../authentication/helpers'
import {
    authLogin,
    authLogout,
    authEntitlementsChecked,
    authRefreshLogin,
    authSessionExpired,
    authNetworkError,
    AuthenticationState,
} from './reducer'
import { UserAuthEvents } from '../../events/user-event-types'
import { Logger } from 'typescript-log'
import {
    BeginLoadingEvent,
    EndLoadingEvent,
} from 'react-ssr-data-loader/dist/cjs/events'
import { RenderTarget } from '@news-mono/web-common'

let hasDoneInitialRefresh = false

export async function checkAccessTokenAndRefresh(params: {
    renditionType: RenderTarget
    accessTokenState: AccessTokenState | undefined
    dispatch: Dispatch<any>
    authState: AuthenticationState
    logger: Logger
    onEvent: (
        authEvent: UserAuthEvents | BeginLoadingEvent | EndLoadingEvent,
    ) => void
    options: {
        invokation: 'manual' | 'auto'
        autoRefreshToken: boolean
    }
    retryCount?: number
}) {
    const {
        accessTokenState,
        dispatch,
        authState,
        logger,
        onEvent,
        options,
        renditionType,
        retryCount = 2,
    } = params
    const { invokation, autoRefreshToken } = options

    try {
        if (invokation === 'auto') {
            onEvent({
                type: 'begin-loading-event',
                data: {
                    resourceType: 'auth-check-refresh-token',
                    resourceLoadParamsHash: '',
                },
            })
        }

        // Temporary - ensure we can be logged in for app users because we have bad tokens
        if (renditionType === 'app') {
            const userInfo = retrieveUserInfoToken()
            // If we have a non-expired user token use that to log em in
            if (userInfo) {
                const authState = getAuthStateFromUserInfoToken(
                    userInfo,
                    onEvent,
                )
                dispatch(
                    authLogin({
                        ...authState,
                        entitlements: ['thewest:all'],
                        onEvent: () => {},
                        subscriptionType: 'subscriber',
                    }),
                )
            } else {
                // who is this person? probably batman. :(
                dispatch(
                    authLogin({
                        userName: '',
                        loginProvider: '',
                        socialProviders: '',
                        gigyaId: '',
                        occupantId: '',
                        entitlements: ['thewest:all'],
                        onEvent: () => {},
                        subscriptionType: 'subscriber',
                    }),
                )
            }
            return
        }

        const { isLoggedIn, hasAttemptedValidation } = authState

        if (!accessTokenState) {
            logger.debug(
                { invokation },
                'No Access token cookie found, logging out',
            )
            dispatch(authEntitlementsChecked())
            isLoggedIn && dispatch(authLogout({ onEvent }))
            return
        }

        const forcedExpiry =
            typeof location !== 'undefined' &&
            location.search.indexOf('force_access_token_expiry') !== -1

        const isTokenExpired =
            accessTokenState.expiryDate < new Date().getTime() / 1000 ||
            forcedExpiry

        try {
            if (
                (autoRefreshToken && isTokenExpired) ||
                !hasDoneInitialRefresh
            ) {
                logger.debug({ invokation }, 'Refreshing token')
                hasDoneInitialRefresh = true

                const response = await new Promise<number>((resolve) => {
                    const xhr = new XMLHttpRequest()
                    xhr.open('POST', authRefreshPath, true)

                    //Send the proper header information along with the request
                    xhr.setRequestHeader('Content-Type', 'application/json')
                    xhr.setRequestHeader('Accept', 'application/json')
                    xhr.withCredentials = true

                    xhr.onreadystatechange = function () {
                        // Call a function when the state changes.
                        if (this.readyState === XMLHttpRequest.DONE) {
                            resolve(this.status)
                        }
                    }

                    xhr.send()
                })

                if (response !== 200) {
                    // If the authentication server responds with a non-200 error
                    // we've now entered a state we can't recover from. The users session will be expired
                    // and the stale cookies cleared.
                    dispatch(authSessionExpired({ onEvent }))

                    // Lets retry if required
                    // A 401 Unauthorized means their refresh token is missing or invalid so retrying won't help
                    if (response !== 401 && retryCount > 0) {
                        setTimeout(
                            () =>
                                checkAccessTokenAndRefresh({
                                    ...params,
                                    retryCount: retryCount - 1,
                                }),
                            5000,
                        )
                    }
                    return
                }

                logger.debug({ invokation }, 'Token refreshed')
            }

            const userInfo = retrieveUserInfoToken()
            dispatch(authEntitlementsChecked())
            logger.debug({ userInfo, invokation }, 'Logging user in')

            if (userInfo) {
                const authState = getAuthStateFromUserInfoToken(
                    userInfo,
                    onEvent,
                )
                dispatch(authRefreshLogin(authState))
                logger.debug(
                    { userInfo, invokation },
                    'userToken / authTokens refreshed successfully',
                )
            }
        } catch (error) {
            dispatch(authEntitlementsChecked())
            // Recovery should be possible when a network error occurs.
            // Flag a network error and on the next refresh attempt try again.
            dispatch(authNetworkError({ onEvent }))
            return
        }
    } finally {
        if (invokation === 'auto') {
            onEvent({
                type: 'end-loading-event',
                data: {
                    resourceType: 'auth-check-refresh-token',
                    resourceLoadParamsHash: '',
                },
            })
        }
    }
}
